import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Image as ImageIcon, Loader2, Monitor, Search, Check, PenLine, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { uploadSharedImage, supabase } from '../../services/authService';
import { pointService } from '../../services/pointService';
import { fetchPexelsImages } from '../../services/pexelsService';
import { clearCache } from '../../services/storageService';
import { translateArticleMulti } from '../../services/gemini';
import { t } from '../../i18n';
import { ImageWithFallback } from '../Common/ImageWithFallback';

interface StadiumPostModalProps {
    isOpen: boolean;
    onClose: () => void;
    teamId?: string;
    onPostSuccess: () => void;
}

const PEXELS_KEYWORDS = [
    "soccer game action", "soccer fans", "soccer player celebration", "soccer player shooting",
    "soccer training", "soccer stadium crowd", "soccer sidelines", "soccer ball"
];

export const StadiumPostModal: React.FC<StadiumPostModalProps> = ({ isOpen, onClose, teamId, onPostSuccess }) => {
    const { user } = useAuth();
    const { lang } = useLanguage();
    const { showToast } = useToast();
    
    const [content, setContent] = useState('');
    const [isAnimating, setIsAnimating] = useState(false);
    
    const [attachedImage, setAttachedImage] = useState<File | null>(null);
    const [pexelsImageUrl, setPexelsImageUrl] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [showImageMenu, setShowImageMenu] = useState(false);
    const [showPexelsModal, setShowPexelsModal] = useState(false);
    const [isPexelsAnimating, setIsPexelsAnimating] = useState(false);
    const [pexelsImages, setPexelsImages] = useState<any[]>([]);
    const [pexelsPage, setPexelsPage] = useState(1);
    const [loadingPexels, setLoadingPexels] = useState(false);
    const [hasMorePexels, setHasMorePexels] = useState(true);
    const [currentPexelsQuery, setCurrentPexelsQuery] = useState('');
    
    const [isPosting, setIsPosting] = useState(false);
    
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageMenuRef = useRef<HTMLDivElement>(null);

    const MAX_PEXELS_COUNT = 100; 
    const PEXELS_BATCH_SIZE = 50; 

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => setIsAnimating(true), 10);
        } else {
            setIsAnimating(false);
            // Reset state when closing
            setContent('');
            setAttachedImage(null);
            setPexelsImageUrl(null);
            setPreviewUrl(null);
            setShowImageMenu(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (imageMenuRef.current && !imageMenuRef.current.contains(e.target as Node)) {
                setShowImageMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [content]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) { 
            setAttachedImage(file); 
            setPexelsImageUrl(null); 
            setPreviewUrl(URL.createObjectURL(file)); 
            setShowImageMenu(false); 
        }
    };

    const loadPexelsBatch = useCallback(async (query: string, page: number, isInitial: boolean = false) => {
        if (loadingPexels || pexelsImages.length >= MAX_PEXELS_COUNT) return;
        setLoadingPexels(true);
        try {
            const result = await fetchPexelsImages(query, PEXELS_BATCH_SIZE, page);
            if (isInitial) setPexelsImages(result.photos);
            else setPexelsImages(prev => [...prev, ...result.photos]);
            const currentTotal = isInitial ? result.photos.length : pexelsImages.length + result.photos.length;
            setHasMorePexels(result.photos.length === PEXELS_BATCH_SIZE && currentTotal < MAX_PEXELS_COUNT);
        } catch (e) {
            console.error("Error loading images:", e);
        } finally {
            setLoadingPexels(false);
        }
    }, [loadingPexels, pexelsImages.length]);

    const handlePexelsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMorePexels || loadingPexels) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            const nextPage = pexelsPage + 1;
            setPexelsPage(nextPage);
            loadPexelsBatch(currentPexelsQuery, nextPage);
        }
    };

    const handleOpenPexels = () => {
        setShowImageMenu(false);
        setShowPexelsModal(true);
        setPexelsPage(1);
        setPexelsImages([]);
        const randomQuery = PEXELS_KEYWORDS[Math.floor(Math.random() * PEXELS_KEYWORDS.length)];
        setCurrentPexelsQuery(randomQuery);
        setTimeout(() => {
            setIsPexelsAnimating(true);
            loadPexelsBatch(randomQuery, 1, true);
        }, 10);
    };

    const handleClosePexels = () => {
        setIsPexelsAnimating(false);
        setTimeout(() => {
            setShowPexelsModal(false);
            setPexelsImages([]);
        }, 500);
    };

    const handlePost = async () => {
        if (!user || !content.trim() || isPosting) return;

        setIsPosting(true);
        try {
            let finalImageUrl = pexelsImageUrl || null;
            if (attachedImage) {
                try {
                    finalImageUrl = await uploadSharedImage(user.id, attachedImage);
                } catch (imgError) {
                    console.error("Image upload failed:", imgError);
                }
            }

            const publishedAt = new Date().toISOString();
            
            const postPayload: any = {
                type: 'stadium',
                source: 'Stadium',
                image: finalImageUrl,
                published_at: publishedAt,
                teams: teamId ? [teamId] : [],
                user_id: user.id,
                [`title_${lang}`]: 'Stadium Post',
                [`content_${lang}`]: content.trim(),
            };

            const { data: insertedData, error: postError } = await supabase
                .from('news_list')
                .insert([postPayload])
                .select()
                .single();
            
            if (postError) throw postError;

            // Background translation
            const performBackgroundTranslation = async (newsId: string) => {
                try {
                    const translations = await translateArticleMulti('Stadium Post', content, false);
                    if (translations) {
                        const updatePayload = {
                            title_ko: 'Stadium Post',
                            title_en: 'Stadium Post',
                            // title_ja: 'Stadium Post',
                            // title_es: 'Stadium Post',
                            // title_fr: 'Stadium Post',
                            content_ko: translations.ko?.content?.trim() || content.trim(),
                            content_en: translations.en?.content?.trim() || content.trim(),
                            // content_ja: translations.ja?.content?.trim() || content.trim(),
                            // content_es: translations.es?.content?.trim() || content.trim(),
                            // content_fr: translations.fr?.content?.trim() || content.trim(),
                        };
                        await supabase.from('news_list').update(updatePayload).eq('id', newsId);
                    }
                } catch (transError) {
                    console.error("Background translation failed:", transError);
                }
            };

            if (insertedData?.id) {
                performBackgroundTranslation(insertedData.id);
            }

            // Award points
            try {
                const pointResult = await pointService.awardPointsWithLimit(user.id, 10, 'News Feed Post', 100);
                if (pointResult.success && pointResult.pointsEarned > 0) {
                    showToast('success', t(lang, 'feedPostPointEarned').replace('{{amount}}', String(pointResult.pointsEarned)));
                } else {
                    showToast('success', t(lang, 'postSuccess'));
                }
            } catch (pError) {
                showToast('success', t(lang, 'postSuccess'));
            }

            clearCache();
            onPostSuccess();
            onClose();
        } catch (e: any) {
            showToast('error', e.message || t(lang, 'failedToPostNews'));
        } finally {
            setIsPosting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-[400] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose}></div>
            
            <div className={`relative bg-white w-full sm:max-w-lg h-[85vh] sm:h-auto sm:max-h-[90vh] rounded-t-[40px] sm:rounded-[32px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                
                {/* Header */}
                <div className="absolute top-6 right-6 z-20">
                    <button onClick={onClose} className="p-2 bg-slate-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all active:scale-90">
                        <X size={20} strokeWidth={3} />
                    </button>
                </div>

                <header className="p-8 pb-4 border-b border-slate-50 flex flex-col shrink-0">
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">{t(lang, 'stadiumPostTitle')}</h2>
                </header>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-8">
                    <textarea 
                        ref={textareaRef} 
                        placeholder={t(lang, 'stadiumPostPlaceholder')} 
                        className="w-full bg-transparent border-none focus:ring-0 text-lg font-medium text-slate-800 resize-none min-h-[200px] outline-none p-0" 
                        value={content} 
                        onChange={(e) => setContent(e.target.value)} 
                    />
                    
                    {previewUrl && (
                        <div className="relative mt-4">
                            <img src={previewUrl} className="w-full rounded-2xl border border-slate-100 shadow-sm" alt="" />
                            <button 
                                onClick={() => { 
                                    setPreviewUrl(null); 
                                    setAttachedImage(null); 
                                    setPexelsImageUrl(null);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }} 
                                className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full hover:bg-rose-500 transition-all"
                            >
                                <X size={14} strokeWidth={3} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer Action */}
                <div className="p-6 sm:px-8 border-t border-slate-50 bg-white shrink-0 flex items-center gap-4">
                    <div className="relative" ref={imageMenuRef}>
                        <button 
                            onClick={() => setShowImageMenu(!showImageMenu)} 
                            className={`flex items-center justify-center w-12 h-12 rounded-2xl border transition-all active:scale-95 ${showImageMenu ? 'border-rose-500 text-rose-500 bg-rose-50' : 'border-slate-100 text-slate-400 hover:border-slate-200'}`}
                        >
                            <ImageIcon size={22} />
                        </button>
                        
                        {showImageMenu && (
                            <div className="absolute bottom-full left-0 mb-4 w-40 bg-white border border-slate-100 rounded-2xl shadow-xl overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2">
                                <div className="p-1 flex flex-col gap-0.5">
                                    <button 
                                        onClick={() => { fileInputRef.current?.click(); setShowImageMenu(false); }} 
                                        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl text-[11px] font-black uppercase text-slate-600 hover:bg-slate-50"
                                    >
                                        <Monitor size={14} className="text-indigo-500" />
                                        <span>{t(lang, 'gallery') || 'Gallery'}</span>
                                    </button>
                                    <button 
                                        onClick={handleOpenPexels} 
                                        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-xl text-[11px] font-black uppercase text-slate-600 hover:bg-slate-50"
                                    >
                                        <Search size={14} className="text-emerald-500" />
                                        <span>Pexels</span>
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                    </div>

                    <button 
                        onClick={handlePost} 
                        disabled={isPosting || !content.trim()} 
                        className="flex-1 bg-slate-900 text-white h-12 rounded-2xl font-black text-sm uppercase tracking-widest disabled:opacity-20 transition-all flex items-center justify-center gap-2 active:scale-[0.98] shadow-lg shadow-slate-200"
                    >
                        {isPosting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                <span>{t(lang, 'posting') || 'Posting...'}</span>
                            </>
                        ) : (
                            <>
                                <Send size={18} />
                                <span>{t(lang, 'post')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Pexels Modal (Sub-modal) */}
            {showPexelsModal && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end sm:items-center sm:justify-center transition-opacity duration-500 ${isPexelsAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleClosePexels}></div>
                    <div className={`relative bg-white w-full h-[80vh] sm:h-[70vh] sm:max-w-2xl rounded-t-[40px] sm:rounded-[32px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isPexelsAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-6 border-b border-slate-50 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-500 flex items-center justify-center"><ImageIcon size={16}/></div>
                                <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Pexels Gallery</h3>
                            </div>
                            <button onClick={handleClosePexels} className="p-2 text-slate-400 hover:text-slate-900 transition-all"><X size={18} strokeWidth={3} /></button>
                        </header>
                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar" onScroll={handlePexelsScroll}>
                            <div className="grid grid-cols-2 gap-3">
                                {pexelsImages.map((img: any) => (
                                    <div 
                                        key={img.id} 
                                        className="aspect-video rounded-xl overflow-hidden cursor-pointer group relative bg-slate-50"
                                        onClick={() => {
                                            setPexelsImageUrl(img.src.large);
                                            setPreviewUrl(img.src.large);
                                            setAttachedImage(null);
                                            handleClosePexels();
                                        }}
                                    >
                                        <img src={img.src.medium} className="w-full h-full object-cover" alt="" />
                                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <Check className="text-white" size={24} strokeWidth={3} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {loadingPexels && (
                                <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-rose-500" size={20} /></div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
